/**
 * セッション（今日のキューを 1 枚ずつ回す）の画面側の状態。
 * - キューは src/lib/progress の buildDailyQueue で作り、保存しない（progress-store.md §4.4）。
 * - 回答は 1 件ごとに進捗ストアへ記録する（recordAnswer。§1.1、§4.1）。
 * - 不正解のカードは requeueWrong でメモリ上のキューに入れ直す（§4.4 手順 4）。
 */
import type { CardAnswer } from '../../components/cards/answer';
import type { GradeResult } from '../../components/types';
import type { Card } from '../../lib/cards';
import {
  buildDailyQueue,
  requeueWrong,
  type AnswerInput,
  type AnswerResult,
  type StoreNotice,
} from '../../lib/progress';
import type { ProgressView } from './progress-view.svelte';

export interface SessionEntry {
  card: Card;
  /** 不正解で入れ直したカードなら true */
  retry: boolean;
  /** セッション開始時に記録が無かった（新しいカード） */
  fresh: boolean;
  /** 回答前にヒントを 1 つ以上開いた（progress-store.md §3 の hint） */
  hint?: boolean;
  /** 未対応の種別で、記録せずに進めたとき */
  skipped?: boolean;
  result?: GradeResult;
  answer?: CardAnswer;
}

export interface SessionDeps {
  progress: Pick<ProgressView, 'snapshot' | 'today' | 'recordAnswer'>;
  cards: readonly Card[];
  /** 今日のキューの新規に出さないカード（レッスン未完了。lesson-format.md §8.1 の heldNewCards） */
  holdNew?: () => ReadonlySet<string>;
}

/**
 * 判定を Leitner の回答にする（progress-store.md §4.3）。
 * CE（write-ac のコンパイルエラー）は回答として数えないので null。
 */
export function answerResultOf(result: GradeResult): AnswerResult | null {
  if (result.verdict === 'CE') return null;
  return result.verdict === 'AC' ? 'correct' : 'wrong';
}

function answerInput(
  card: Card,
  result: AnswerResult,
  verdict: GradeResult['verdict'],
  hint: boolean,
): AnswerInput {
  const input: AnswerInput = { cardId: card.id, result, hint };
  if (card.type === 'write-ac' && verdict !== 'CE') input.verdict = verdict;
  return input;
}

export function createSession(deps: SessionDeps) {
  const state = $state<{ entries: SessionEntry[]; startedAt: number }>({
    entries: [],
    startedAt: 0,
  });

  function byId(id: string): Card | undefined {
    return deps.cards.find((c) => c.id === id);
  }

  function setEntries(ids: readonly string[], now: number): void {
    const snap = deps.progress.snapshot;
    state.entries = ids
      .map((id) => byId(id))
      .filter((c): c is Card => c !== undefined)
      .map((card) => ({ card, retry: false, fresh: snap.progress.cards[card.id] === undefined }));
    state.startedAt = now;
  }

  return {
    get entries(): readonly SessionEntry[] {
      return state.entries;
    },
    get total(): number {
      return state.entries.length;
    },
    get started(): boolean {
      return state.entries.length > 0;
    },
    get startedAt(): number {
      return state.startedAt;
    },
    /** 今日のキューを作り直して始める。キューが空なら started は false のまま */
    start(now: number = Date.now()): void {
      const snap = deps.progress.snapshot;
      const holdNew = deps.holdNew?.();
      const ids = buildDailyQueue(
        snap.progress,
        deps.cards,
        deps.progress.today(),
        snap.settings,
        holdNew ? { holdNew } : {},
      );
      setEntries(ids, now);
    },
    /**
     * 決まった順のカードで始める（レッスンのドリル。lesson-format.md §8.1）。
     * 見つからない id は飛ばす。回答の扱いは今日のキューと同じ。
     */
    startWith(ids: readonly string[], now: number = Date.now()): void {
      setEntries(
        ids.filter((id) => byId(id)?.draft !== true),
        now,
      );
    },
    /**
     * 0 始まりの位置に回答を記録する。回答として数えるもの（§4.3）は進捗ストアに書き、
     * 不正解なら入れ直す。ストアの知らせ（保存できなかった等）を返す。
     */
    record(
      position: number,
      result: GradeResult,
      answer: CardAnswer,
      opts: { hint?: boolean } = {},
    ): StoreNotice[] {
      const entry = state.entries[position];
      if (!entry || entry.result) return [];
      entry.result = result;
      entry.answer = answer;
      const outcome = answerResultOf(result);
      if (outcome === null) return [];
      const { notices } = deps.progress.recordAnswer(
        answerInput(
          entry.card,
          outcome,
          result.verdict,
          (opts.hint ?? false) || entry.hint === true,
        ),
      );
      if (outcome === 'wrong') {
        const ids = state.entries.map((e) => e.card.id);
        const next = requeueWrong(ids, position);
        let at = next.findIndex((id, i) => id !== ids[i]);
        if (at === -1) at = ids.length;
        state.entries.splice(at, 0, { card: entry.card, retry: true, fresh: false });
      }
      return notices;
    },
    /** ヒントを開いた（CardView の onhint）。採点前のときだけ記録に効く */
    markHint(position: number): void {
      const entry = state.entries[position];
      if (entry && !entry.result) entry.hint = true;
    },
    skip(position: number): void {
      const entry = state.entries[position];
      if (entry && !entry.result) entry.skipped = true;
    },
    /** 最初の未回答（1 始まり）。全部済みなら null */
    firstUnanswered(): number | null {
      const i = state.entries.findIndex((e) => !e.result && !e.skipped);
      return i === -1 ? null : i + 1;
    },
    /** 未回答の枚数 */
    remaining(): number {
      return state.entries.filter((e) => !e.result && !e.skipped).length;
    },
    summary(): { cards: number; correct: number; again: number } {
      const first = state.entries.filter((e) => !e.retry && e.result);
      const unique = (xs: string[]) => xs.filter((x, i) => xs.indexOf(x) === i);
      const ids = unique(state.entries.map((e) => e.card.id));
      const again = unique(
        state.entries.filter((e) => e.result && e.result.verdict !== 'AC').map((e) => e.card.id),
      );
      return {
        cards: ids.length,
        correct: first.filter((e) => e.result?.verdict === 'AC').length,
        again: again.length,
      };
    },
  };
}

export type Session = ReturnType<typeof createSession>;
